/**
 * Curated pantry starter checklist for empty pantries.
 * `itemName` values are submitted to POST /inventory/batch (inventory_service infers category).
 */

/** @typedef {{ itemName: string, defaultChecked: boolean, aliases?: string[] }} PantryStarterItem */

/** @typedef {{ id: string, title: string, subtitle?: string, items: PantryStarterItem[] }} PantryStarterGroup */

/** @type {PantryStarterGroup[]} */
export const PANTRY_STARTER_GROUPS = [
  {
    id: 'basics_opt_out',
    title: 'Usually in the kitchen',
    subtitle: 'Uncheck anything you don’t have.',
    items: [
      { itemName: 'Olive oil', defaultChecked: true },
      { itemName: 'Vegetable oil', defaultChecked: true },
      { itemName: 'Kosher salt', defaultChecked: true },
      { itemName: 'Black pepper', defaultChecked: true },
      { itemName: 'Sugar', defaultChecked: true },
      { itemName: 'Rice', defaultChecked: true },
      { itemName: 'Soy sauce', defaultChecked: true },
      { itemName: 'Vinegar', defaultChecked: true },
      { itemName: 'Honey', defaultChecked: true },
      { itemName: 'Mustard', defaultChecked: true },
      { itemName: 'Mayonnaise', defaultChecked: true },
      { itemName: 'Canned diced tomatoes', defaultChecked: true },
      { itemName: 'Chicken broth', defaultChecked: true },
      { itemName: 'Onions', defaultChecked: true },
      { itemName: 'Garlic', defaultChecked: true },
    ],
  },
  {
    id: 'more_staples',
    title: 'Add if you have them',
    subtitle: 'Pantry, fridge, or freezer.',
    items: [
      { itemName: 'Butter', defaultChecked: false },
      { itemName: 'Milk', defaultChecked: false },
      { itemName: 'Eggs', defaultChecked: false },
      { itemName: 'Cheddar cheese', defaultChecked: false },
      { itemName: 'Plain yogurt', defaultChecked: false },
      { itemName: 'Frozen vegetables', defaultChecked: false },
      { itemName: 'Corn tortillas', defaultChecked: false },
      { itemName: 'Dry beans', defaultChecked: false },
      { itemName: 'Paprika', defaultChecked: false },
      { itemName: 'Dried oregano', aliases: ['Oregano dried'], defaultChecked: false },
      { itemName: 'Cumin', defaultChecked: false },
      { itemName: 'Red pepper flakes', defaultChecked: false },
    ],
  },
  {
    id: 'proteins',
    title: 'Proteins',
    subtitle: 'Check what you have in the fridge or freezer right now.',
    items: [
      { itemName: 'Chicken breast', defaultChecked: false },
      { itemName: 'Chicken thighs', defaultChecked: false },
      { itemName: 'Ground beef', defaultChecked: false },
      { itemName: 'Steak', defaultChecked: false },
      { itemName: 'Ground turkey', defaultChecked: false },
      { itemName: 'Pork chops', defaultChecked: false },
      { itemName: 'Pork shoulder', defaultChecked: false },
      { itemName: 'Bacon', defaultChecked: false },
      { itemName: 'Tofu', defaultChecked: false },
      { itemName: 'Canned tuna', defaultChecked: false },
      { itemName: 'Salmon', defaultChecked: false },
      { itemName: 'Frozen shrimp', defaultChecked: false },
    ],
  },
  {
    id: 'fresh_produce',
    title: 'Fresh produce',
    subtitle: 'Only what you expect to finish in the next week or two.',
    items: [
      { itemName: 'Carrots', defaultChecked: false },
      { itemName: 'Celery', defaultChecked: false },
      { itemName: 'Broccoli', defaultChecked: false },
      { itemName: 'Spinach', defaultChecked: false },
      { itemName: 'Lettuce', defaultChecked: false },
      { itemName: 'Tomatoes', defaultChecked: false },
      { itemName: 'Bell peppers', defaultChecked: false },
      { itemName: 'Lemons', defaultChecked: false },
      { itemName: 'Cilantro', defaultChecked: false },
      { itemName: 'Green onions', defaultChecked: false },
      { itemName: 'Ginger', defaultChecked: false },
      { itemName: 'Limes', defaultChecked: false },
      { itemName: 'Potatoes', defaultChecked: false },
      { itemName: 'Mushrooms', defaultChecked: false },
      { itemName: 'Zucchini', defaultChecked: false },
      { itemName: 'Corn', defaultChecked: false },
      { itemName: 'Avocados', defaultChecked: false },
      { itemName: 'Shallots', defaultChecked: false },
      { itemName: 'Parsley', defaultChecked: false },
      { itemName: 'Basil', defaultChecked: false },
    ],
  },
]

/** Quick-add tabs for empty pantry (PantryQuickAddTabs). */
export const PANTRY_STARTER_TABS = [
  {
    key: 'staples',
    label: 'Staples',
    groupIds: ['basics_opt_out', 'more_staples'],
    extraItems: [],
  },
  {
    key: 'produce',
    label: 'Produce',
    groupIds: ['fresh_produce'],
    extraItems: [],
  },
  {
    key: 'protein',
    label: 'Protein',
    groupIds: ['proteins'],
    extraItems: [
      { itemName: 'Chickpeas', defaultChecked: false },
      { itemName: 'Black beans', defaultChecked: false },
      { itemName: 'Lentils', defaultChecked: false },
    ],
  },
]

/** Optional last step: add items that were not on the checklists. */
export const PANTRY_STARTER_EXTRAS_TAB = {
  key: 'more',
  label: 'More',
}

export const PANTRY_STARTER_FLOW_TABS = [
  ...PANTRY_STARTER_TABS,
  PANTRY_STARTER_EXTRAS_TAB,
]
